<template>
  <div
    ref="wrapper"
    class="relative -mt-16 w-full overflow-hidden"
    :class="{ 'pointer-events-none': mobile }"
    v-waypoint="{
      active: true,
      callback: eventWaypoint,
      options: intersectionOptions,
    }"
    @mouseover="$store.commit('SET_SHOW_MY_CURSOR', true)"
    @mouseleave="$store.commit('SET_SHOW_MY_CURSOR', false)"
  >
    <div ref="three" class="absolute w-full h-full"></div>
    <div
      ref="css"
      class="
        absolute
        w-full
        h-full
        flex
        items-center
        justify-center
        pointer-events-none
      "
    >
      <div ref="video" style="height: 320px; width: 586px">
        <vue-plyr :options="videoOptions" ref="plyr">
          <video crossorigin playsinline>
            <source
              size="720"
              src="https://bafybeiedweozej2p5oymvdc3yyv7ytywhyzoxoyw32lxshass37laou7ra.ipfs.infura-ipfs.io/"
              type="video/mp4"
            />
          </video>
        </vue-plyr>
      </div>
    </div>
  </div>
</template>

<script>
import {
  PerspectiveCamera,
  Scene,
  WebGLRenderer,
  PMREMGenerator,
  BufferAttribute,
  Group,
  Vector3,
  Quaternion,
  MeshBasicMaterial,
  MeshStandardMaterial,
  TextureLoader,
  NearestFilter,
  ClampToEdgeWrapping,
} from 'three'
import {
  CSS3DRenderer,
  CSS3DObject,
} from 'three/examples/jsm/renderers/CSS3DRenderer'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import isMobile from 'ismobilejs'

let camera,
  controls,
  scene,
  cssScene,
  renderer,
  cssRenderer,
  videoObject,
  modelObject

export default {
  props: ['livestream'],

  data() {
    return {
      mobile: isMobile(window.navigator).any,
      intersectionOptions: {
        root: null,
        rootMargin: '0px 0px 0px 0px',
        threshold: [0],
      },
      videoOptions: {
        muted: true,
        autoplay: true,
        playsinline: true,
        controls: [],
        loop: { active: true },
      },
      shouldRender: true,
    }
  },

  computed: {
    id() {
      if (this.$props.livestream.livestream_active == 'true') {
        return this.$props.livestream.livestream_id
      } else {
        return this.$props.livestream.placeholder_id
      }
    },
  },

  async mounted() {
    this.initSceneCSS()
    this.initScene()
    this.animate()
  },

  unmounted() {
    this.cleanup()
  },

  methods: {
    eventWaypoint({ going }) {
      if (this._isBeingDestroyed) return

      if (going === this.$waypointMap.GOING_OUT) {
        this.shouldRender = false
        this.$refs.plyr.player.pause()
      } else {
        this.shouldRender = true
        this.$refs.plyr.player.play()
      }
    },

    animate() {
      requestAnimationFrame(this.animate)
      this.render()
    },

    render() {
      if (!this.shouldRender) return

      controls.update()

      if (this.mobile && modelObject && videoObject) {
        const rotSpeed = 0.001
        modelObject.rotation.y += rotSpeed
        videoObject.rotation.z += -rotSpeed
      }

      // render css scene
      cssRenderer.render(cssScene, camera)

      if (videoObject) {
        const p = new Vector3()
        const q = new Quaternion()
        videoObject.getWorldPosition(p)
        videoObject.getWorldQuaternion(q)

        // get world forward direction of video object
        const fwdDir = new Vector3(0, 0, 1)
        fwdDir.applyQuaternion(q)

        // get world direction to camera
        const camDir = camera.position.clone().sub(p).normalize()

        // angle bewteen
        const angle = fwdDir.angleTo(camDir)

        // show/hide based on angle from camera
        if (angle < Math.PI / 2) {
          if (videoObject.element.style.visibility !== 'visible') {
            videoObject.element.style.visibility = 'visible'
          }
        } else {
          if (videoObject.element.style.visibility !== 'hidden') {
            videoObject.element.style.visibility = 'hidden'
          }
        }
      }

      renderer.render(scene, camera)
    },

    initSceneCSS() {
      cssScene = new Scene()

      cssRenderer = new CSS3DRenderer()
      cssRenderer.setSize(window.innerWidth, window.innerHeight)
      this.$refs.css.appendChild(cssRenderer.domElement)

      this.$refs.plyr.muted = true
      this.$refs.plyr.player.on('ready', () => {
        this.$refs.plyr.player.play()
      })
    },

    async initScene() {
      scene = new Scene()

      // camera
      camera = new PerspectiveCamera(
        60,
        window.innerWidth / window.innerHeight,
        0.1,
        10000
      )

      // renderer
      renderer = new WebGLRenderer({ antialias: true })
      renderer.setSize(window.innerWidth, window.innerHeight)
      renderer.setPixelRatio(window.devicePixelRatio)
      renderer.setClearColor(0x000000)

      this.$refs.three.appendChild(renderer.domElement)

      // controls
      camera.position.set(-300, -300, 700)
      controls = new OrbitControls(camera, renderer.domElement)
      controls.enableZoom = false
      controls.enablePan = false
      controls.enableDamping = true
      controls.dampingFactor = 0.08
      controls.target.set(0, 0, 0)
      if (this.mobile) {
        controls.enabled = false
      }
      controls.update()

      // resize
      this.onWindowResize()
      window.addEventListener('resize', this.onWindowResize)

      // video
      const video = this.$refs.video

      videoObject = new CSS3DObject(video)
      videoObject.position.set(0, -19, 0)

      videoObject.rotation.set(Math.PI / 2, 0, 0)

      videoObject.element.style.opacity = 0
      videoObject.element.style.pointerEvents = 'none'

      const plyr = videoObject.element.querySelector('.plyr')
      plyr.style.borderRadius = '5px'

      cssScene.add(videoObject)

      const textureLoader = new TextureLoader().setPath('assets/textures/')

      // load textures
      const [envTexture, lightMap] = await Promise.all([
        textureLoader.loadAsync('photo_studio_01_1k.jpg'),
        textureLoader.loadAsync('Model_texture.jpg'),
      ])

      const pmremGenerator = new PMREMGenerator(renderer)
      pmremGenerator.compileEquirectangularShader()
      const envMap = pmremGenerator.fromEquirectangular(envTexture).texture
      scene.environment = envMap
      envTexture.dispose()
      pmremGenerator.dispose()

      const gltfLoader = new GLTFLoader().setPath('assets/models/')
      const dracoLoader = new DRACOLoader()
      dracoLoader.setDecoderPath('assets/js/draco/gltf/')
      gltfLoader.setDRACOLoader(dracoLoader)

      const [model, displayInner, displayFrame] = await Promise.all([
        gltfLoader.loadAsync('Model.glb'),
        gltfLoader.loadAsync('Display_inner.glb'),
        gltfLoader.loadAsync('Display_frame.glb'),
      ])

      lightMap.flipY = false
      lightMap.magFilter = NearestFilter
      lightMap.minFilter = NearestFilter
      lightMap.wrapT = ClampToEdgeWrapping
      lightMap.wrapS = ClampToEdgeWrapping

      model.scene.traverse(function (child) {
        if (child.isMesh) {
          const uvs = child.geometry.attributes.uv.array
          child.geometry.setAttribute('uv2', new BufferAttribute(uvs, 2))
          child.geometry.attributes.uv2.needsUpdate = true

          child.material = new MeshBasicMaterial({
            color: child.material.color,
            map: lightMap,
          })
        }
      })

      displayInner.scene.traverse(function (child) {
        if (child.isMesh) {
          child.material = new MeshBasicMaterial({ color: '#000000' })
        }
      })

      modelObject = new Group()

      // TODO: debug envMap reflection
      displayFrame.scene.traverse(function (child) {
        if (child.isMesh) {
          child.material = new MeshStandardMaterial({
            color: 0x191919,
            envMap: envMap,
            roughness: 0,
          })
        }
      })

      model.scene.scale.set(39.1, 39.1, 39.1)

      modelObject.add(model.scene)
      modelObject.add(displayInner.scene)
      modelObject.add(displayFrame.scene)

      scene.add(modelObject)

      const s = 10.35
      modelObject.scale.set(s, s, s)
      modelObject.rotation.set(0, -Math.PI / 2, 0)

      videoObject.element.style.opacity = 1
    },

    onWindowResize() {
      const w = window.innerWidth
      let h

      if (this.mobile) {
        h =
          window.orientation === 90 || window.orientation === -90
            ? screen.width * 0.9
            : screen.height * 0.9
      } else {
        h = window.innerHeight
      }

      const cDis = w > h ? 900 : 1600
      const cPos = camera.position.clone().normalize().multiplyScalar(cDis)
      camera.position.set(cPos.x, cPos.y, cPos.z)
      controls.update()

      this.$refs.wrapper.style.height = `${h}px`

      camera.aspect = w / h
      camera.updateProjectionMatrix()
      cssRenderer.setSize(w, h)
      renderer.setSize(w, h)
    },

    cleanup() {
      window.removeEventListener('resize', this.onWindowResize)

      renderer.displose()

      scene.traverse((object) => {
        if (!object.isMesh) return

        object.geometry.dispose()

        if (object.material.isMaterial) {
          this.cleanMaterial(object.material)
        } else {
          // an array of materials
          for (const material of object.material) this.cleanMaterial(material)
        }
      })
    },
  },

  cleanMaterial: (material) => {
    material.dispose()

    // dispose textures
    for (const key of Object.keys(material)) {
      const value = material[key]
      if (value && typeof value === 'object' && 'minFilter' in value) {
        value.dispose()
      }
    }
  },
}
</script>
